/**
 * Section 10 — Indicators Reference.
 *
 * One entry per chart overlay. Each entry has: what it is, the math
 * (formula in plain language), how to read it, common pitfalls, and a
 * worked example. The reference users open when they hover an unfamiliar
 * line on a chart and ask "what is that?".
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 0.5, color: 'text.primary' }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const F = ({ children }: { children: React.ReactNode }) => (
  <Box component="pre" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.82rem', p: 1, my: 0.75, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 0.75 }}>{children}</Box>
);

/** Reusable indicator block — title + plain-English + formula + read + pitfall. */
const Indicator: React.FC<{
  id: string;
  name: string;
  what: React.ReactNode;
  formula: React.ReactNode;
  read: React.ReactNode;
  pitfall?: React.ReactNode;
}> = ({ id, name, what, formula, read, pitfall }) => (
  <Box id={`anchor-${id}`} sx={{ mb: 3.5, scrollMarginTop: 80 }}>
    <H3>{name}</H3>
    <Box sx={{ pl: 1 }}>
      <P><strong>What:</strong> {what}</P>
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
          Formula
        </Typography>
        {formula}
      </Box>
      <P><strong>How to read:</strong> {read}</P>
      {pitfall && <P><strong>Pitfall:</strong> {pitfall}</P>}
    </Box>
  </Box>
);

const Section_indicators: GuideSection = {
  id: 'indicators',
  label: 'Indicators Reference',
  group: 'reference',
  icon: <ShowChartIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Indicators Reference</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Every overlay available in <OpenInApp to="/charts" label="Charts → Chart Settings → Overlays" />.
        Each entry: <em>what it is</em>, the formula in plain language, how to read it, and the gotcha
        that's caused the most "wait, what?" support questions.
      </Typography>

      <GuideCallout kind="info">
        Indicators are <em>analytics</em> — they don't generate signals on their own. The strategies
        (Sniper / RockBand / DailyBlast) combine multiple indicators into entry rules. See
        <OpenInApp to="/app-guide#concepts" label="Core Concepts → Indicators vs strategies" />.
      </GuideCallout>

      <H id="trend">Trend indicators</H>

      <Indicator
        id="ema"
        name="EMA — Exponential Moving Average"
        what="Smoothed average that weights recent bars more than old ones. earngush computes its EMAs over a LinReg(close, 14) source (a lightly de-noised close), not the raw close."
        formula={<><F>{`src   = LinReg(close, 14)
α     = 2 / (N + 1)
EMA_t = α × src_t + (1 − α) × EMA_{t−1}`}</F>
          earngush exposes four configurable EMAs (default periods <strong>7 / 21 / 51 / 200</strong>) — toggle
          visibility per-EMA, each in a distinct colour + thickness, in Chart Settings → Indicators → EMAs.</>}
        read="A rising EMA (slope > 0) = price is trending up. Faster EMA crossing slower from below = bullish crossover; the reverse = bearish."
        pitfall="EMAs lag by half their period. A 200-period EMA on a 5m chart reflects roughly the last 8 hours, not 'right now'. Don't use long EMAs for scalp decisions."
      />

      <Indicator
        id="avgcloud"
        name="AvgCloud"
        what={<>Shaded ribbon that visualises the current trend regime. Green for up, red for down. Since R72, anchored on CMid instead of the older C-POC + EMA-200 + EMA-128 mix.</>}
        formula={<><F>{`upper = max(CMid, CMid + k × σ_CTF)
lower = min(CMid, CMid − k × σ_CTF)
fill colour =
  green if close > CMid
  red   if close < CMid`}</F>
          where σ_CTF is the BB stdev on the current timeframe.</>}
        read={<>Cloud colour = trend regime. Cloud thickness ≈ recent volatility. Price diving through a thick green cloud signals strong selling pressure; touches of the cloud edge are pullbacks.</>}
        pitfall="The cloud is derived from CMid, which itself uses three timeframes. On exotic intervals (45m / 90m composites) the LTF/HTF maths uses the next-standard timeframe — slight differences from a pure single-TF computation."
      />

      <Indicator
        id="cmid"
        name="CMid — Combined Midline"
        what={<>3-timeframe Bollinger-basis blend that drives AvgCloud's colour (and, via the AvgCloud constituent, feeds the OverView score that RockBand reads). The R129 Sniper no longer uses CMid.</>}
        formula={<><F>{`For each TF in [LTF, CTF, HTF]:
  basis_i = SMA(close, 20) on TF_i
  σ_i     = stdev(close, 20) on TF_i
  w_i     = 1 / σ_i              # inverse-volatility weight

CMid = Σ(basis_i × w_i) / Σ(w_i)`}</F>
          Default TF ratios: LTF = TF/4, CTF = TF (the chart's interval), HTF = TF × 4.</>}
        read="A single value per candle. close > CMid = bullish regime; close < CMid = bearish. The cross between the two flips the AvgCloud colour."
        pitfall={<>Pre-R71 the underlying <code>sma()</code> had a sliding-window null-drain bug that made CMid 10× too high when the source had leading nulls. Fixed in R71 — but if you ever see CMid wildly off the chart, that's the symptom to look for.</>}
      />

      <Indicator
        id="lr-hama"
        name="LR_HAMA Band — Linear-Regression Heikin-Ashi MA Band"
        what="Smoothed trend envelope used by the DailyBlast strategy. Combines Linear-Regression line fit with Heikin-Ashi smoothing."
        formula={<><F>{`HA candles smoothed from regular OHLC
HAMA = LinReg(HA_close, N)        # least-squares fit
upper = HAMA + σ_offset × stdev(HA_close, N)
lower = HAMA − σ_offset × stdev(HA_close, N)`}</F>
          Defaults: N=50, σ_offset=1.0.</>}
        read="HAMA line = the smoothed trend. Upper/lower envelope = the statistical edge of recent HA range. Price touching the envelope = exhaustion candidate or pullback opportunity."
        pitfall="HA-based indicators repaint slightly on the current (open) candle because HA needs the close. The envelope is reliable only after bar close."
      />

      <H id="volatility">Volatility indicators</H>

      <Indicator
        id="bollinger"
        name="Bollinger Band"
        what="Classic volatility envelope around a moving average."
        formula={<><F>{`basis = SMA(close, period)
σ      = stdev(close, period)
upper  = basis + k × σ
lower  = basis − k × σ`}</F>
          Defaults: period 20, k 2.0.</>}
        read={<>Price spends ~95% of bars inside the band (assuming normal distribution). A close <em>outside</em> the band is statistically rare — either fresh breakout or exhaustion. Used by RockBand strategy as primary trigger.</>}
        pitfall="Bollinger bands assume returns are normally distributed — they're not, especially in crypto. Extreme-vol bars happen more often than the model says; don't bet the farm on a single band-pierce reversal."
      />

      <H id="volume">Volume-derived indicators</H>

      <Indicator
        id="c-poc"
        name="C-POC — Current Point of Control"
        what="The single price level at which the most volume was traded during the visible session."
        formula={<>Build a volume profile (volume-by-price histogram) over the visible bars; the highest bar's price = POC. Drawn as a horizontal line on the chart.</>}
        read="POC acts as a magnet — price often returns to it intraday. Useful as a take-profit reference when in a position aligned with the trend."
        pitfall="POC shifts as new volume comes in; it's not a fixed level. On longer-period charts the POC computation can be heavy — disable it on the 1d/1w to keep render fast."
      />

      <Indicator
        id="hvn"
        name="HVN — High-Volume Node (user-drawn)"
        what="A horizontal price level you manually draw on the chart by clicking the HVN toolbar button + then clicking on the price. Persists per-user, per-symbol."
        formula={<>Not computed — you place it where you've identified historical volume concentration. Stays drawn across all subsequent chart loads of that symbol.</>}
        read="HVN levels act as S/R. Feeds the Order Flow absorption detector — when a volume burst hits price near an HVN and price doesn't break, you get an S-abs or R-abs tag in the OF panel."
        pitfall="No automatic detection — you have to drop the levels yourself. Re-evaluate weekly; old HVNs become irrelevant as market structure shifts."
      />

      <Indicator
        id="flow-strip"
        name="Order Flow ribbon"
        what="Per-candle taker-buy vs taker-sell volume histogram at the bottom of the chart. 7-state colour palette."
        formula={<><F>{`For each candle bucket:
  buyPct = buyVol / (buyVol + sellVol)

state =
  buyPct ≥ 0.75      → Bulls in control (deep green)
  buyPct 0.60 — 0.75 → Bulls holding    (med green)
  buyPct 0.45 — 0.60 ∧ trend up   → Bulls fading
  buyPct 0.45 — 0.55              → Neutral
  buyPct 0.40 — 0.55 ∧ trend down → Bears fading
  buyPct 0.25 — 0.40 → Bears holding   (med red)
  buyPct ≤ 0.25      → Bears in control (deep red)

bar height ∝ total volume (clipped to 95th percentile)`}</F></>}
        read="Bar colour = who's in control. Bar height = how busy that candle was. A pattern of tall green bars then a sudden tall red bar = supply hit + worth attention."
        pitfall={<>One chip cycles Flow / HVol / Vol: on Delta it goes Off → Flow → HVol → Vol → Off; on other exchanges (no Flow) it is Off → HVol → Vol → Off. They're mutually exclusive lower-pane views.</>}
      />

      <Indicator
        id="volume"
        name="Vol — Raw Volume"
        what="The classic per-bar volume histogram at the bottom of the price pane — EVERY bar, coloured green when the regular candle closed up and red when it closed down. Computed from the raw (Normal) candles, so it looks the same on any display candle type. Third stop on the Flow / HVol / Vol chip."
        formula={<><F>{`bar height = raw volume
colour     = close ≥ open ? green : red   # from the RAW (Normal) candle`}</F></>}
        read="Height = how much traded that bar. Rising volume into a move = conviction; a climax bar (unusually tall) often marks exhaustion. Pair colour + height with price."
        pitfall={<>Plain volume, no smoothing — for the surge/effort read use HVol (below); for taker buy-vs-sell pressure use the Order Flow ribbon (Delta only). Uses sensible green/red defaults (no settings needed).</>}
      />

      <Indicator
        id="vol"
        name="HVol — High / Weighted Volume (volume × close)"
        what="Lower-pane volume meter that weights each bar's volume by price. Shows ONLY spike bars plus two EMAs with a bull/bear band. Second stop on the Flow / HVol / Vol chip (cycles Off → Flow → HVol → Vol → Off on Delta; Off → HVol → Vol → Off elsewhere)."
        formula={<><F>{`wVol      = volume × close
src       = LinReg(wVol, 7)        # pre-smoothing
Fast EMA  = EMA(src, 3)
Slow EMA  = EMA(src, 21)
spike     = raw wVol > Slow × 2     # only these bars are drawn
band      = filled between Fast & Slow (bull when Fast ≥ Slow)`}</F>
          The LinReg period (7), EMA lengths (3 / 21) and spike multiplier (2×) are fixed.</>}
        read="A spike bar = an outsized weighted-volume event (effort). Fast above Slow (bull band) = rising participation; Fast below Slow = fading. Spike colour follows the bar's direction (bull/bear)."
        pitfall={<>Spikes are surges, not direction by themselves — confirm with price and the EMA band. EMAs on/off and band/spike colours + opacity are in Chart Settings → Indicators → HVol. <strong>R120:</strong> the volume vote is also an OverView <em>constituent</em> — a GREEN spike latches its vote <code>+1</code> until the next RED spike (then <code>−1</code>), held in between. Toggle it in OverView → Contributors.</>}
      />

      <Indicator
        id="av-band"
        name="AV_Band — Anchored Volume Band"
        what="Price-pane band anchored to Weighted-Volume spike zones and steered by the OverView trend. Two HMAs of an 'Effective Line' form a bull/bear band around price."
        formula={<><F>{`# On each WVol spike bar, hold the bar's High/Low as a zone
#   (carried forward until the next spike).
# Effective Line per bar, by chart-TF OverView score — two selectable rules:
#   Logic 1 (default):  score > 20 → zone HIGH,  score < -20 → zone LOW,  else mid
#   Logic 2 (inverted): score > 20 → zone LOW,   score < -20 → zone HIGH, else mid
# Two HMAs of the Effective Line:
Fast HMA = HMA(effective, 14)     # configurable
Slow HMA = HMA(effective, 100)    # configurable
band     = filled between them (bull when Fast HMA ≥ Slow HMA)`}</F></>}
        read="The band's slope + colour read like a trend channel anchored to where real volume changed hands. Bullish (Fast ≥ Slow) = trend up; bearish below. The optional Effective Line shows the raw stepped anchor the HMAs smooth. Logic 1 vs Logic 2 lets you compare the trend-following vs mean-reversion framing of the same zones."
        pitfall={<>Needs the OverView score, so it also turns on the OverView computation. In Chart Settings → Indicators → AV_Band you can pick the Effective Line rule (Logic 1 / Logic 2), set the fast/slow HMA periods, line width and Effective-Line thickness, choose the line style (<em>staircase</em> or <em>smooth line</em>), toggle Show Fast HMA / Show Slow HMA / Show Band / Show Effective Line independently, and set band bull/bear colours + opacity. <strong>R120:</strong> AV_Band is also an OverView <em>constituent</em> — it votes <code>+1</code> when close &gt; Fast HMA, <code>−1</code> when below, using the same Effective-Line rule you pick here. To avoid a feedback loop (the Effective Line is derived from the OverView score) the voter resolves its Effective Line from the score <em>excluding AV_Band itself</em>. Toggle it in OverView → Contributors.</>}
      />

      <H id="reference">Reference-line indicators</H>

      <Indicator
        id="cpr"
        name="CPR — Central Pivot Range"
        what="Three horizontal lines derived from yesterday's H/L/C. Acts as intraday S/R."
        formula={<><F>{`Pivot = (H + L + C) / 3
BC    = (H + L) / 2            # Bottom Central
TC    = Pivot + (Pivot − BC)   # Top Central`}</F>
          Drawn as three lines: TC at top, Pivot in middle, BC at bottom.</>}
        read="When TC and BC are far apart (wide CPR) → expected range day, mean reversion plays. When TC and BC are close together (narrow CPR) → directional / trend day; breakout plays."
        pitfall="CPR is calibrated for index futures (where it's most useful). On crypto perpetuals it's mathematically valid but markets don't respect daily-pivot psychology there — read more cautiously."
      />

      <H id="comparison">Comparison indicators</H>

      <Indicator
        id="relstrength"
        name="RelStrength — Relative Strength vs benchmark"
        what="Ratio of chart symbol's recent trend slope vs a benchmark's, normalised so the first valid pair = 100."
        formula={<><F>{`symLR  = LinReg(close, 14)        on chart symbol
benchLR = LinReg(close, 14)       on benchmark
ratio_t = symLR_t / benchLR_t
RS_t    = 100 × ratio_t / ratio_{first valid t}

EMA band: ema(RS, period) ± visible band fill`}</F>
          Renders on its own pane below the price chart. Default benchmark configurable in Chart Settings;
          for NSE charts the default is NIFTY, for crypto BTCUSD.</>}
        read="RS > 100 = chart symbol is outperforming the benchmark since the start of the window. Rising RS line = outperformance accelerating. Useful for sector / pair-trading decisions."
        pitfall={<>Pre-R74 the EMA seeded NaN from null values, making the band invisible. Fixed in R74 by switching to a null-aware <code>ema()</code> helper.</>}
      />

      <Indicator
        id="chgn"
        name="ChgN% — N-bar % change"
        what="Rolling N-bar percentage change of close. Useful momentum gauge that's framework-free (no smoothing assumptions)."
        formula={<><F>{`ChgN%_t = 100 × (close_t − close_{t−N}) / close_{t−N}

EMA band: ema(ChgN%, period) ± band fill`}</F>
          Default N = 24 bars (so on 1h chart this is 24-hour % change).</>}
        read={<>Positive ChgN% = price is higher than N bars ago. The EMA band gives "normal" vs "extreme" context — price above the upper band = moving fast.</>}
        pitfall="With N=24 the indicator has 24 leading nulls. R74 fixed the band rendering issue these nulls caused; if you customise N, expect that many bars of warm-up before the band displays."
      />

      <Indicator
        id="wrpct"
        name="WR% — paired Williams %R EMAs (Round 116, zone logic R116b)"
        what={<>Two Williams %R lines, each smoothed by its own EMA, plotted together on a fixed −100..0 strip. R116b introduced a <strong>zone-based</strong> trigger that uses the slow EMA's position vs the −50 mid line AND the fast EMA's slope, instead of the simple fast/slow crossover. Default source is <code>LinReg(close, 14)</code>; defaults stay <code>fast = WR%(21)+EMA(13)</code>, <code>slow = WR%(200)+EMA(13)</code>.</>}
        formula={<><F>{`WR%(N) = 100 × (close − highest(N)) / (highest(N) − lowest(N))      // range [-100, 0]
fastEMA  = ema( WR%(fastLength) , fastEma )
slowEMA  = ema( WR%(slowLength) , slowEma )

Zone classification per bar (R116b):
  BUY      when slowEMA[i] > -50  AND  fastEMA[i] > fastEMA[i-1]
  SHORT    when slowEMA[i] < -50  AND  fastEMA[i] < fastEMA[i-1]
  NEUTRAL  otherwise

OverView / Trend / Strategy / Scan / Backtest:
  BUY     → 'up'   vote (weighted by wrPct weight)
  SHORT   → 'down' vote
  NEUTRAL → 'na'   — WR% ABSTAINS, no score contribution

Chart band fill:
  BUY     → bullColor    (e.g. green)
  SHORT   → bearColor    (e.g. red)
  NEUTRAL → grey
-50 mid line is thicker / amber — the load-bearing zone reference.

OB    = -20    (overbought guide line, fixed)
OS    = -80    (oversold guide line, fixed)`}</F>
          Source: <code>close</code> or <code>LinReg(close, 14)</code> (default).</>}
        read={<>The −50 mid line is the load-bearing reference for the zone decision; the indicator paints it thicker + amber and surrounds the strip with a faint bull tint (above −50) / bear tint (below −50) so the boundary is visible even when the band fill is grey. Read sequence: (1) Is the SLOW EMA on the BUY-eligible side of −50? (2) Is the FAST EMA rising/falling in agreement? If both align → coloured band (green = BUY, red = SHORT). If they disagree → grey band = NEUTRAL = WR% abstains in scoring. The strip is pinned to −100..0 so what you see is what scoring sees.</>}
        pitfall="A flat fast EMA (fast[i] == fast[i-1]) produces NEUTRAL — abstain, not a directional vote. The slow %R length defaults to 200, so the slow EMA needs roughly 200 + EMA-warmup closed bars to seed; on thin-history symbols the slow EMA stays null and the indicator paints grey until warm-up completes."
      />

      <Indicator
        id="linreg"
        name="Linear Regression (line + channel)"
        what="Least-squares straight-line fit through the last N close prices. Used as a 'fair value' reference + as base for envelopes."
        formula={<>Standard simple linear regression: <code>close = a + b × bar_index</code>. <code>a, b</code> minimise the sum of squared residuals. Line plotted at the fitted values; channel is the line ± k × stdev of residuals.</>}
        read="A rising LinReg line = uptrend over the window. Channel width = noise / dispersion. Price near the channel edge = extreme; price returning to the line = mean-reversion in progress."
        pitfall="Linear regression assumes a linear trend over the window. On a turning market it overshoots — the line keeps extending in the old direction for a while after price has actually turned."
      />

      <Indicator
        id="vob"
        name="VOB — Volume-Trend Order Block"
        what={<>A custom <strong>supertrend</strong> trend filter combined with <strong>volume-split order blocks</strong>. The supertrend draws a trailing-stop line with a directional cloud between price and the line; on confirmed swing pivots (in the matching trend) it plots support/resistance order-block zones, each split into a "Sell %" (top) and "Buy %" (bottom) half by the buy/sell volume balance.</>}
        formula={<><F>{`custom_atr = SMA(high − low, stLen)
upper = hl2 + mult·custom_atr ; lower = hl2 − mult·custom_atr
trend_stop trails: up-trend → max(lower, prev) ; down-trend → min(upper, prev)
flip when close crosses the prior trend_stop

Pivot (pivotLen each side) in-trend → Order Block:
  up-trend, swing low  → bull OB: top = min(open,close) at pivot, bot = top − custom_atr
  down-trend, swing high→ bear OB: bot = max(open,close) at pivot, top = bot + custom_atr
  split = bot + (top−bot)·buy_ratio   (buy_ratio = buy vol ÷ total vol over the pivot window)
  top half label = "Sell: (1−buy_ratio)%"  ·  bottom half = "Buy: buy_ratio%"`}</F></>}
        read={<>Cloud colour = current trend (teal bull / pink bear). An order block is a zone price reacted from — the dominant half (Buy in a bull OB, Sell in a bear OB) is shaded stronger. Price returning to a zone is a <strong>retest</strong>: a cross back above a bull OB top (Buy % ≥ your filter) marks a bullish retest; a cross below a bear OB bottom (Sell % ≥ filter) a bearish one. A new block can't overlap the active one; the active block extends right and (optionally) deletes once price fully breaks through it.</>}
        pitfall={<>Order blocks confirm <em>pivotLen bars after</em> the swing (a pivot needs that many bars on each side), so they appear with a deliberate lag — this is what makes them non-repainting. The Buy/Sell % is from chart candle volume; on instruments without real volume it falls back to 50/50. Computed on closed raw candles, so it's identical across display candle types.</>}
      />

      <H id="osc-htf">BBR &amp; Wave oscillators — the higher timeframe in the same pane</H>
      <P>
        The <strong>BBR</strong> and <strong>Wave</strong> oscillator chips draw their curve for the chart&rsquo;s own
        timeframe. Each can also draw the same oscillator for the <strong>higher timeframe mapped on the Strategies
        page</strong> (for example 1H → 1D), switched on per oscillator in <strong>Chart Settings → BBR</strong> or{' '}
        <strong>→ Wave</strong> (&ldquo;Also show the higher timeframe&rdquo;). With both chips and both switches on you
        see four curves: BBR and Wave for the chart&rsquo;s timeframe, and BBR and Wave for the higher one.
      </P>
      <P>
        The higher-timeframe curve sits on the <strong>same overbought / oversold rows</strong> as the chart&rsquo;s own
        curve and is labelled with its timeframe on the axis (&ldquo;BBR 1D&rdquo;, &ldquo;Wave 1D&rdquo;). Each daily value
        is drawn flat across that day&rsquo;s hourly bars, coloured <strong>rising / falling</strong> exactly like the
        chart&rsquo;s own curve — a daily step up paints the rise colour, a step down the fall colour. It is told apart
        from the chart&rsquo;s curve by its thicker, lighter staircase and its timeframe badge. The{' '}
        <strong>current, unfinished day is dashed</strong>: it is rebuilt from the chart&rsquo;s own bars, so it moves
        with every tick until the day closes. It uses the same lengths, levels and source candles (BBR × Wave card) as
        the chart&rsquo;s curve.
      </P>
      <P>
        To read the higher timeframe <strong>alone</strong>, switch off &ldquo;Show the chart timeframe BBR / Wave&rdquo;
        in the same card. Only that curve goes: the overbought / oversold rows, the zone shading and the BW▲ / BD▼ / X
        marks on the price chart stay, because hiding a line does not change a signal. The small signal dots on the
        oscillator and the BBR-vs-Wave pane tint describe the chart-timeframe curves, so they hide with them.
      </P>
      <GuideCallout kind="info" title="A reading aid, not a signal">
        A flat daily step in history shows the value the day finished at. During that day it was still moving. So read
        it as context. The BW▲ / BD▼ / X marks are computed on the chart&rsquo;s own timeframe only and do not change
        when you switch this on. In <strong>Bar Replay</strong> only higher-timeframe bars that had finished by the
        playhead are used, and the current one is rebuilt from the bars before it, so replay never shows the future.
        Nothing is drawn when there is no higher timeframe (on 1W, the top of the ladder). An optional tint in the BBR ×
        Wave card shades the bars where the higher timeframe is also oversold or overbought.
      </GuideCallout>

      <H id="settings-overview">Per-overlay configuration in Chart Settings</H>
      <P>
        Every overlay listed above is toggleable + tunable in Chart Settings → Overlays. Tweakable knobs
        per overlay typically include: period, σ (where applicable), source (close / hl2 / hlc3), colour,
        line width, label visibility. Changes apply immediately and persist per-user.
      </P>

      <GuideExample title="A multi-indicator chart read — BTCUSD 5m, 14:30 IST">
        Open BTCUSD on the 5m chart with AvgCloud + CMid + Bollinger + EMAs (50, 200) + Order Flow ribbon
        enabled. You see:<br />
        <br />
        AvgCloud is <strong>green</strong> · CMid line is just below price, sloping up · Bollinger bands
        widening · EMA-50 above EMA-200 (golden-cross since yesterday) · Last 4 ribbon bars are
        <strong> deep green</strong>.<br />
        <br />
        Read: full agreement across trend (CMid green), volatility expansion (BB widening), longer-trend
        (EMA50 &gt; EMA200), and order flow (deep-green ribbon = aggressive buy taker). High-conviction long
        regime. A Sniper signal here would be a continuation entry; a Sniper SHORT signal would be a
        warning to fade your bias.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Trend indicators',     anchor: 'trend',          body: 'trend indicators ema avgcloud cmid lr hama' },
    { heading: 'EMA',                  anchor: 'ema',            body: 'ema exponential moving average period 7 21 51 200 linreg close source' },
    { heading: 'AvgCloud',             anchor: 'avgcloud',       body: 'avgcloud trend cloud green red regime' },
    { heading: 'CMid',                 anchor: 'cmid',           body: 'cmid combined midline three timeframe bollinger' },
    { heading: 'LR_HAMA Band',         anchor: 'lr-hama',        body: 'lr hama band linear regression heikin ashi moving average envelope' },
    { heading: 'Volatility',           anchor: 'volatility',     body: 'volatility bollinger band' },
    { heading: 'Bollinger Band',       anchor: 'bollinger',      body: 'bollinger band period sigma upper lower' },
    { heading: 'Volume',               anchor: 'volume',         body: 'volume c-poc hvn flow ribbon' },
    { heading: 'VOB',                  anchor: 'vob',            body: 'vob volume trend order block supertrend cloud pivot sell buy percent retest zone' },
    { heading: 'C-POC',                anchor: 'c-poc',          body: 'c-poc current point of control volume profile' },
    { heading: 'HVN',                  anchor: 'hvn',            body: 'hvn high volume node user drawn level replaces line tool' },
    { heading: 'Order Flow ribbon',    anchor: 'flow-strip',     body: 'order flow ribbon strip seven state palette taker bias flow vol cycle' },
    { heading: 'Vol (Weighted Volume)', anchor: 'vol',           body: 'vol weighted volume wvol volume close linreg fast slow ema spike bars band flow vol chip' },
    { heading: 'AV_Band',              anchor: 'av-band',        body: 'av band anchored volume band effective line overview score hma fast slow zone' },
    { heading: 'Reference lines',      anchor: 'reference',      body: 'reference lines cpr' },
    { heading: 'CPR',                  anchor: 'cpr',            body: 'cpr central pivot range pivot tc bc' },
    { heading: 'Comparison',           anchor: 'comparison',     body: 'comparison relstrength chgn' },
    { heading: 'RelStrength',          anchor: 'relstrength',    body: 'relative strength benchmark ratio linreg ema band' },
    { heading: 'ChgN%',                anchor: 'chgn',           body: 'change n percent rolling momentum ema band' },
    { heading: 'Linear Regression',    anchor: 'linreg',         body: 'linear regression linreg least squares fit channel' },
    { heading: 'BBR & Wave — higher timeframe', anchor: 'osc-htf', body: 'bbr wave oscillator higher timeframe htf strategies mapping same pane forming dashed confluence replay' },
  ],
};

export default Section_indicators;
